import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSession } from "../session/SessionContext";
import { EmptyState, ErrorState, PermissionDeniedState } from "../components/states";
import { UsageLoading } from "../components/consumption/UsageLoading";
import { ConsumptionSummary } from "../components/consumption/ConsumptionSummary";
import { formatClockTime } from "../components/consumption/formatters";
import { useSelectedPeriod } from "../hooks/useSelectedPeriod";
import { previousMonthStart } from "../components/consumption/usagePace";
import {
  acknowledgeThreshold,
  fetchMyConsumption,
  fetchMyHistory,
  type ConsumptionQueryResult,
  type HistoryPeriodResponse,
  type MyConsumptionResponse,
  type MyHistoryResponse,
} from "../api/reporting";
import { fetchMyAllowance } from "../api/organisationReporting";
import { unchangedOr, usePolling } from "../hooks/usePolling";

type PageState =
  | { kind: "loading" }
  | {
      kind: "loaded";
      response: MyConsumptionResponse;
      effectiveAllowanceUsd: number | null;
      /** The month immediately before this one, or null when the history call failed or does not hold it. */
      previousPeriod: HistoryPeriodResponse | null;
    }
  | { kind: "denied" }
  | { kind: "error"; message: string };

/**
 * The history entry for the month immediately before `periodStart`, or null when the history call
 * did not succeed or does not hold that month.
 *
 * @param history - The result of the history call.
 * @param periodStart - The first day of the period being shown.
 * @returns The previous month's history entry, or null.
 */
function previousPeriodFor(history: ConsumptionQueryResult<MyHistoryResponse>, periodStart: string): HistoryPeriodResponse | null {
  if (history.kind !== "ok" || !Array.isArray(history.response.periods)) {
    return null;
  }
  const previousStart = previousMonthStart(periodStart);
  return history.response.periods.find((period) => period.periodStart === previousStart) ?? null;
}

/**
 * The "My consumption" landing screen (phase 12, `AIA-81`, `SC-1`): an engineer signs in and sees
 * their consolidated AI spend for the current period, broken down by tool, with zero interactions.
 * Every state — loading, the honest empty state, partial (one vendor behind), denied, and loaded —
 * is rendered distinctly, per the phase brief.
 */
export function MyConsumptionPage(): React.JSX.Element {
  const { snapshot } = useSession();
  const [state, setState] = useState<PageState>({ kind: "loading" });
  const [dismissingThreshold, setDismissingThreshold] = useState(false);
  const [periodStart] = useSelectedPeriod();

  // Every active, signed-in user can see their own consumption. The permission is resolved by the
  // server automatically instead of requiring explicit role assignment, so a user can always reach
  // their personal spend view without an administrative bundle.
  const hasPermission = snapshot.status === "signed-in";

  // A quiet load refreshes the figures in place, without the loading layout, after something on the page changed them.
  const load = useCallback(async (signal?: AbortSignal, quiet = false) => {
    if (!quiet) {
      setState({ kind: "loading" });
    }
    const [result, allowanceResult, historyResult] = await Promise.all([
      fetchMyConsumption(periodStart, signal),
      fetchMyAllowance(signal),
      // A failed or denied history call must not fail the page: it only hides the month-over-month comparison.
      fetchMyHistory(signal),
    ]);
    if (result.kind === "aborted") {
      return;
    }
    // A quiet refresh that fails keeps the figures shown; the next one tries again.
    if (quiet && result.kind !== "ok") {
      return;
    }
    switch (result.kind) {
      case "denied":
        setState({ kind: "denied" });
        return;
      case "error":
        setState({ kind: "error", message: result.message });
        return;
      case "ok": {
        const next: PageState = {
          kind: "loaded",
          response: result.response,
          effectiveAllowanceUsd: allowanceResult.kind === "ok" ? allowanceResult.response.effectiveAllowanceUsd : null,
          previousPeriod: previousPeriodFor(historyResult, result.response.periodStart),
        };
        // Unchanged figures keep the page as it is, so its charts are not redrawn.
        setState((previous) => unchangedOr(previous, next));
      }
    }
  }, [periodStart]);

  useEffect(() => {
    const controller = new AbortController();
    if (hasPermission) {
      void load(controller.signal);
    }
    return () => controller.abort();
  }, [hasPermission, load]);

  usePolling(() => load(undefined, true), hasPermission && state.kind === "loaded");

  const handleDismissThreshold = useCallback(
    (thresholdPercent: number) => {
      setDismissingThreshold(true);
      void acknowledgeThreshold(thresholdPercent).finally(() => {
        setDismissingThreshold(false);
        // The banner has already closed itself; the refresh only brings the figures up to date, so it is quiet.
        void load(undefined, true);
      });
    },
    [load],
  );

  return (
    <div>
      <ConsumptionPageBody
        hasPermission={hasPermission}
        state={state}
        onRetry={() => void load()}
        onDismissThreshold={handleDismissThreshold}
        dismissingThreshold={dismissingThreshold}
      />
    </div>
  );
}

function ConsumptionPageBody({
  hasPermission,
  state,
  onRetry,
  onDismissThreshold,
  dismissingThreshold,
}: {
  hasPermission: boolean;
  state: PageState;
  onRetry: () => void;
  onDismissThreshold: (thresholdPercent: number) => void;
  dismissingThreshold: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();

  if (!hasPermission) {
    return <PermissionDeniedState title={t("consumption.denied.title")} description={t("consumption.denied.description")} />;
  }

  switch (state.kind) {
    case "loading":
      return <UsageLoading />;
    case "denied":
      return <PermissionDeniedState title={t("consumption.denied.title")} description={t("consumption.denied.description")} />;
    case "error":
      return <UsageLoading error={<ErrorState title={t("consumption.errorTitle")} description={state.message} onRetry={onRetry} />} />;
    case "loaded":
      return state.response.hasAnyData ? (
        <ConsumptionSummary
          response={state.response}
          onDismissThreshold={onDismissThreshold}
          dismissingThreshold={dismissingThreshold}
        effectiveAllowanceUsd={state.effectiveAllowanceUsd}
          previousPeriod={state.previousPeriod}
        />
      ) : (
        <EmptyState
          title={t("consumption.emptyTitle")}
          description={
            state.response.firstFiguresExpectedAt
              ? t("consumption.emptyDescriptionWithTime", { time: formatClockTime(state.response.firstFiguresExpectedAt) })
              : t("consumption.emptyDescription")
          }
        />
      );
  }
}
